import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * AdminCounterpartyAudit — controllo contropartite.
 * Scansiona gli affari ESEGUITI con contropartite e segnala i giocatori-contropartita
 * che non risultano nella squadra del venditore (mai spostati / rimasti indietro).
 * Permette di "riparare" spostando il giocatore nella squadra corretta.
 */
export default function AdminCounterpartyAudit() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(true);
    const [repairing, setRepairing] = useState("");

    const load = async () => {
        setLoading(true); setErr("");
        try {
            const { data } = await api.get("/market/audit/counterparty");
            setData(data);
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const repair = async (issue) => {
        setRepairing(issue.player_id); setErr("");
        try {
            await api.post("/market/audit/counterparty/repair", {
                deal_id: issue.deal_id,
                player_id: issue.player_id,
            });
            await load();
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally {
            setRepairing("");
        }
    };

    const issues = data?.issues || [];

    return (
        <div data-testid="admin-counterparty-audit-page">
            <div className="op-label" style={{ color: "rgb(var(--tc-9d7cff))" }}>// CONTROLLO CONTROPARTITE</div>
            <h2 className="op-heading text-3xl mt-2 mb-2">Contropartite rimaste indietro</h2>
            <p className="op-mono text-xs text-t8f95a3 mb-5 max-w-3xl leading-relaxed">
                Scansiona gli affari <strong className="text-tffffff">già eseguiti</strong> con contropartite e segnala i
                giocatori-contropartita che <strong className="text-tffffff">non risultano nella squadra del venditore</strong>
                {" "}(es. scambi vecchi mai applicati). Premi <span className="text-t7ce38b">Ripara</span> per spostarli nella squadra corretta.
            </p>

            <div className="flex items-center gap-3 mb-4">
                <button
                    onClick={load}
                    disabled={loading}
                    className="op-mono text-xs uppercase tracking-wider px-4 py-2 border border-t9d7cff text-t9d7cff hover:bg-t9d7cff/10 transition-colors disabled:opacity-40"
                    data-testid="cp-audit-refresh"
                >
                    {loading ? "Scansione…" : "↻ Riesegui scansione"}
                </button>
                {data && (
                    <span className="op-mono text-xs text-t8f95a3" data-testid="cp-audit-count">
                        {issues.length === 0 ? "✓ Nessun problema trovato" : `${issues.length} contropartite da sistemare`}
                    </span>
                )}
            </div>

            {err && (
                <div className="border border-tff3b30 bg-tff3b30/10 text-tff3b30 text-xs p-2 mb-4" data-testid="cp-audit-err">{err}</div>
            )}

            {issues.length > 0 && (
                <div className="op-card overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-[10px] uppercase tracking-wider text-t8f95a3 border-b border-t22252a">
                            <tr>
                                <th className="py-2 px-3 text-left">Contropartita</th>
                                <th className="py-2 px-3 text-left">Stato</th>
                                <th className="py-2 px-3 text-left">Squadra attuale</th>
                                <th className="py-2 px-3 text-left">Dovrebbe stare a</th>
                                <th className="py-2 px-3 text-left">Affare (target)</th>
                                <th className="py-2 px-3 text-right">Azione</th>
                            </tr>
                        </thead>
                        <tbody>
                            {issues.map((it) => (
                                <tr key={`${it.deal_id}-${it.player_id}`} className="border-b border-t1a1d24 hover:bg-t0f1115" data-testid={`cp-issue-${it.player_id}`}>
                                    <td className="py-2 px-3 text-tffffff">{it.player_name}</td>
                                    <td className="py-2 px-3">
                                        <span className={`op-mono text-[10px] uppercase px-1.5 py-0.5 border ${it.status === "missing" ? "border-tff3b30/50 text-tff3b30" : "border-tffb020/50 text-tffb020"}`}>
                                            {it.status === "missing" ? "non esiste" : "squadra errata"}
                                        </span>
                                    </td>
                                    <td className="py-2 px-3 op-mono text-tc9ccd3">{it.current_team_name || "—"}</td>
                                    <td className="py-2 px-3 op-mono text-t7ce38b">{it.seller_team_name || "—"}</td>
                                    <td className="py-2 px-3 op-mono text-t8f95a3">{it.target_player || "—"} <span className="text-t5c6370">(da {it.buyer_team_name || "—"})</span></td>
                                    <td className="py-2 px-3 text-right">
                                        {it.repairable ? (
                                            <button
                                                onClick={() => repair(it)}
                                                disabled={repairing === it.player_id}
                                                className="op-mono text-[10px] uppercase tracking-wider px-3 py-1 border border-t7ce38b text-t7ce38b hover:bg-t7ce38b hover:text-black transition-colors disabled:opacity-40"
                                                data-testid={`cp-repair-${it.player_id}`}
                                            >
                                                {repairing === it.player_id ? "..." : "Ripara"}
                                            </button>
                                        ) : (
                                            <span className="op-mono text-[10px] text-t5c6370" title="Il giocatore non esiste più: reinseriscilo a mano con 'Aggiungi giocatore'">
                                                reinserire a mano
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
